'use client';

// First-run onboarding checklist — shown on the Work desk for trial
// users until all steps are done (or the user dismisses it).
// Each step is one tap to the right place; steps auto-tick when done.

import React, { useEffect, useMemo, useState } from "react";
import { Check, Circle, X, ArrowRight, PartyPopper } from "lucide-react";
import { api } from "./api";

const FONT = { fontFamily: "'Hind', 'IBM Plex Sans', system-ui, sans-serif" };

// Finishing either variant of the Ashraya flow-setup guide counts as doing
// the "flow" step (owner decision, 8 Aug 2026 — was manual-tick only before).
const FLOW_TOUR_IDS = ["jigyasa-flow-setup-solo", "jigyasa-flow-setup-team", "jigyasa-essential-settings"];

export const OnboardingChecklist = ({ org, onNavigate, isSolo = false }) => {
  const dismissKey = `jigyasa.onboarding.dismissed.${org?.id || "x"}`;
  const manualKey = `jigyasa.onboarding.manual.${org?.id || "x"}`;
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(dismissKey) === "1"; } catch { return false; }
  });
  // Steps the user ticked off themselves, plus the flow step which also
  // auto-ticks when the Ashraya flow-setup guide is completed end-to-end.
  const [manualDone, setManualDone] = useState(() => {
    try { return JSON.parse(localStorage.getItem(manualKey)) || {}; } catch { return {}; }
  });
  const [memberCount, setMemberCount] = useState(null);
  const [enquiryCount, setEnquiryCount] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/team/members").then((r) => alive && setMemberCount((r.data || []).length)).catch(() => {});
    api.get("/enquiries").then((r) => alive && setEnquiryCount((r.data || []).length)).catch(() => {});
    return () => { alive = false; };
  }, []);

  const markDone = (key) => {
    setManualDone((prev) => {
      const next = { ...prev, [key]: true };
      try { localStorage.setItem(manualKey, JSON.stringify(next)); } catch {}
      return next;
    });
  };

  useEffect(() => {
    const onTour = (e) => {
      const evt = e?.detail || {};
      if (evt.type === "tour_completed" && FLOW_TOUR_IDS.includes(evt.tour_id)) markDone("flow");
    };
    window.addEventListener("ashraya:tour", onTour);
    return () => window.removeEventListener("ashraya:tour", onTour);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const profile = org?.profile || {};
  const hasCompanyDetails = Boolean(profile.address || profile.city || profile.logo);

  const steps = useMemo(() => [
    // No. 1 on purpose: the enquiry flow must match the business BEFORE the
    // first enquiry. The CTA fires an Ashraya guided walkthrough.
    {
      key: "flow",
      title: "Set up how your enquiries flow",
      blurb: "Your follow-up steps, products and sources — I'll walk you through it.",
      done: Boolean(manualDone.flow),
      cta: "Guide me",
      go: "flow-guide",
    },
    {
      key: "details",
      title: "Add your company details",
      blurb: "Your logo and address — any time you like.",
      done: hasCompanyDetails || Boolean(manualDone.details),
      cta: "Open settings",
      go: "settings",
    },
    // The team step is meaningless for a one-person (Solo) business — hide it.
    ...(isSolo ? [] : [{
      key: "team",
      title: "Invite your team",
      blurb: "Add the people who'll handle enquiries with you.",
      done: (memberCount != null && memberCount > 1) || Boolean(manualDone.team),
      cta: "Go to Team",
      go: "team",
    }]),
    {
      key: "enquiry",
      title: "Add your first enquiry",
      blurb: "Name, phone, where they heard of you. Done in 10 seconds.",
      done: (enquiryCount != null && enquiryCount > 0) || Boolean(manualDone.enquiry),
      cta: "Add one now",
      go: "operator",
    },
  ], [manualDone, hasCompanyDetails, memberCount, enquiryCount, isSolo]);

  const doneCount = steps.filter((s) => s.done).length;
  const allDone = doneCount === steps.length;

  if (dismissed || allDone) return null;

  const dismiss = () => {
    try { localStorage.setItem(dismissKey, "1"); } catch {}
    setDismissed(true);
  };

  return (
    <div
      data-testid="onboarding-checklist"
      className="mx-auto mb-4 max-w-3xl rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm"
      style={FONT}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <PartyPopper size={18} className="text-rose-600" />
          <h3 className="text-base font-semibold text-zinc-900">Let's get you set up</h3>
        </div>
        <button data-testid="onboarding-dismiss" onClick={dismiss} className="rounded-md p-1 text-zinc-400 hover:bg-zinc-100" title="Hide this">
          <X size={16} />
        </button>
      </div>
      <p className="mt-1 text-sm text-zinc-500">
        {doneCount} of {steps.length} done — a couple of quick steps and you're flying.
      </p>

      <div className="mt-4 space-y-2">
        {steps.map((s) => (
          <div
            key={s.key}
            data-testid={`onboarding-step-${s.key}`}
            className={`flex items-center gap-3 rounded-xl border p-3 ${s.done ? "border-emerald-200 bg-emerald-50" : "border-zinc-200 bg-zinc-50"}`}
          >
            {s.done ? (
              <Check size={18} className="shrink-0 text-emerald-600" />
            ) : (
              <button
                type="button"
                data-testid={`onboarding-markdone-${s.key}`}
                onClick={() => markDone(s.key)}
                title="Mark as done"
                className="group/tick shrink-0 rounded-full p-0.5 transition-colors hover:bg-emerald-100"
              >
                <Circle size={18} className="text-zinc-300 transition-colors group-hover/tick:text-emerald-500" />
              </button>
            )}
            <div className="min-w-0 flex-1">
              <div className={`text-sm font-medium ${s.done ? "text-emerald-800 line-through" : "text-zinc-900"}`}>{s.title}</div>
              {!s.done && <div className="text-xs text-zinc-500">{s.blurb} <span className="text-zinc-400">· tap the circle when you're done</span></div>}
            </div>
            {!s.done && (
              <button
                data-testid={`onboarding-go-${s.key}`}
                onClick={() => onNavigate(s.go)}
                className="inline-flex shrink-0 items-center gap-1 rounded-full bg-zinc-900 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-zinc-700"
              >
                {s.cta} <ArrowRight size={13} />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
